import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Subscription } from 'rxjs';

import { DomEventsService } from '@app/common/services/dom-events.service';
import { MpTooltipPosition } from '@app/types/mp-tooltip.types';

@Directive({
  selector: '[mpTooltip]'
})
export class MpTooltipDirective implements OnDestroy, OnInit {
  private domEventsService = inject(DomEventsService);
  private elementRef = inject(ElementRef);

  @Input()
  set mpTooltip(html: string | string[]) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipReference.innerHTML = Array.isArray(html) ? html.join('<br><br>') : html;
    this.a11yReference.innerHTML = this.richToPoor(this.tooltipReference.innerHTML);
  }

  @Input()
  set mpTooltipPosition(pos: MpTooltipPosition) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipPosition = pos;
    this.positionTooltip();
  }

  @Input()
  set mpTooltipMargin(m: number) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipMargin = m;
    this.positionTooltip();
  }

  @Input()
  set mpTooltipClass(className: string | string[]) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipReference.classList.forEach((c) => {
      if (c !== 'wt-tooltip') {
        this.tooltipReference.classList.remove(c);
      }
    });

    if (typeof className === 'string') {
      this.tooltipReference.classList.add(className as string);
    } else if (Array.isArray(className) && className.length) {
      this.tooltipReference.classList.add(...className);
    }
  }

  @Input()
  set mpTooltipDisabled(disabled: boolean) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipDisabled = disabled;

    if (this.tooltipDisabled) {
      this.hideTooltip();
      this.removeA11yReference();
    } else {
      this.addA11yReference();
    }
  }

  @Input()
  set mpTooltipDelayMs(tooltipDelayMs: number) {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipDelayMs = tooltipDelayMs;
  }

  private subscriptions: Subscription[] = [];
  private isPlatformBrowser = false;
  private tooltipVisible = false;
  private tooltipDisabled = false;
  private tooltipPosition: MpTooltipPosition = MpTooltipPosition.Below;
  private tooltipMargin = 5;
  private tooltipDelayMs = 500;
  private a11yReferenceAdded = false;
  private showTooltipTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly uniqueId: string;
  private readonly tooltipReference: HTMLDivElement;
  private readonly a11yReference: HTMLDivElement;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.uniqueId = `mp-tooltip-${new Date().valueOf()}`;
    this.isPlatformBrowser = isPlatformBrowser(platformId);

    if (!this.isPlatformBrowser) {
      return;
    }

    this.tooltipReference = document.createElement('div');
    this.tooltipReference.classList.add('mp-tooltip');
    this.a11yReference = document.createElement('div');
    this.a11yReference.setAttribute('id', `tooltipA11y_${this.uniqueId}`);
    this.a11yReference.classList.add('sr-only');
  }

  ngOnInit(): void {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.addA11yReference();
    this.subscriptions.push(this.domEventsService.eventStreams.windowResize.subscribe(() => this.hideTooltip()));
  }

  ngOnDestroy(): void {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.removeA11yReference();
    this.hideTooltip();
    if (this.showTooltipTimeout) {
      clearTimeout(this.showTooltipTimeout);
    }
    this.subscriptions.forEach((s) => s?.unsubscribe());
  }

  @HostListener('mouseenter')
  showTooltip(): void {
    if (this.tooltipDisabled || this.tooltipVisible) {
      return;
    }

    this.positionTooltip();
    this.showTooltipTimeout = setTimeout(() => {
      document.body.append(this.tooltipReference);
      this.tooltipVisible = true;
    }, this.tooltipDelayMs);
  }

  @HostListener('mouseleave')
  hideTooltip(): void {
    if (this.showTooltipTimeout) {
      clearTimeout(this.showTooltipTimeout);
    }

    if (!this.tooltipVisible) {
      return;
    }

    this.tooltipReference.remove();
    this.tooltipVisible = false;
  }

  private positionTooltip(): void {
    this.tooltipReference.removeAttribute('style');
    const elementBoundingRect = this.elementRef.nativeElement.getBoundingClientRect();
    const elementTop = elementBoundingRect.top;
    const elementLeft = elementBoundingRect.left;
    const elementWidth = elementBoundingRect.width;
    const elementHeight = elementBoundingRect.height;

    switch (this.tooltipPosition) {
      case MpTooltipPosition.Above:
        this.tooltipReference.setAttribute('style', `top: ${elementTop}px; left: ${elementLeft + elementWidth / 2}px; transform: translate(-50%, calc(-100% - ${this.tooltipMargin}px))`);
        break;

      case MpTooltipPosition.Below:
        this.tooltipReference.setAttribute('style', `top: ${elementTop + elementHeight}px; left: ${elementLeft + elementWidth / 2}px; transform: translate(-50%, ${this.tooltipMargin}px)`);
        break;

      case MpTooltipPosition.Left:
        this.tooltipReference.setAttribute('style', `left: ${elementLeft}px; top: ${elementTop + elementHeight / 2}px; transform: translate(calc(-100% - ${this.tooltipMargin}px), -50%)`);
        break;

      case MpTooltipPosition.Right:
        this.tooltipReference.setAttribute('style', `left: ${elementLeft + elementWidth}px; top: ${elementTop + elementHeight / 2}px; transform: translate(${this.tooltipMargin}px, -50%)`);
        break;
    }
  }

  private addA11yReference(): void {
    if (this.tooltipDisabled || this.a11yReferenceAdded) {
      return;
    }

    document.body.append(this.a11yReference);
    this.elementRef.nativeElement.setAttribute('aria-describedby', `tooltipA11y_${this.uniqueId}`);
    this.a11yReferenceAdded = true;
  }

  private removeA11yReference(): void {
    if (!this.a11yReferenceAdded) {
      return;
    }

    this.elementRef.nativeElement.removeAttribute('aria-describedby');
    this.a11yReference.remove();
    this.a11yReferenceAdded = false;
  }

  private richToPoor(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.innerText;
  }
}
